// features/lab-feature.js
// ═══════════════════════════════════════════════════════════
// LAB — universal research workstation.
//
// Generic feature available to every Shift user. Each user has
// their own private Lab containing their own research projects.
// The methodology layer (10 moves, confidence labels, Phase A,
// paired-case discipline, calibration registry) is universal and
// locked. Everything else — projects, cases, hypotheses, records,
// dossiers, predictions — is per-user and private by default.
//
// Visual design (2026-05-01): 4-color palette only.
//   Steel  #6b8fa8 — structure, data, methodology, instruments
//   Gold   #c8a96e — your identity, active CTA, locked output
//   Sage   #7a9e82 — ready / passing / positive verdict
//   Terra  #c46050 — Phase A kill / contradiction / error
// Other Shift palette colors (teal, purple, sand) intentionally
// not used here — Lab earns its visual identity by restraint.
//
// Architecture: docs/LAB_ARCHITECTURE.md
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import LabLibraryFeature from './lab-library-feature';

// 4-color palette for Lab + neutrals (per CLAUDE.md design tokens)
const C = {
  // Brand — 4 colors only
  steel: '#6b8fa8',
  gold:  '#c8a96e',
  sage:  '#7a9e82',
  terra: '#c46050',
  // Text neutrals
  text1: '#d4d4d4', text2: '#a8a8a8', text3: '#888', text4: '#666',
  // Surface neutrals
  bg0: '#07070a', bg2: '#111118', bg3: '#171723',
  border3: '#272736', border4: '#1e1e2a',
};

const FONT_SERIF = 'Georgia, serif';
const FONT_MONO  = '"SF Mono", "Roboto Mono", "Courier New", monospace';
const FONT_SANS  = 'system-ui, -apple-system, "Segoe UI", sans-serif';

// Subtle graph-paper background — barely visible, signals "workbench"
const GRID_BG = `
  linear-gradient(rgba(107, 143, 168, 0.025) 1px, transparent 1px),
  linear-gradient(90deg, rgba(107, 143, 168, 0.025) 1px, transparent 1px),
  ${C.bg0}
`;

// ──────────────────────────────────────────────────────────
// Main feature
// ──────────────────────────────────────────────────────────
export default function LabFeature({ session, supabase, userRecord }) {
  const [labSection, setLabSection] = useState('workbench'); // workbench | library
  const [projects, setProjects] = useState(null); // null = loading
  const [activeProject, setActiveProject] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const accessToken = session?.access_token;

  const loadProjects = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await fetch('/api/lab/projects', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.status === 401) { setProjects([]); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setProjects(json.projects || []);
    } catch (err) {
      console.error('[Lab] load projects:', err);
      setProjects([]);
    }
  }, [accessToken]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  // Library section — fill the entire .shift-main height via flex column
  if (labSection === 'library') {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ flexShrink: 0, padding: '6px 16px', background: '#07070a', borderBottom: '1px solid #272736' }}>
          <button
            type="button"
            onClick={() => setLabSection('workbench')}
            style={{ background: 'none', border: 'none', color: '#6b8fa8', cursor: 'pointer', fontSize: 12 }}
          >
            ← Lab
          </button>
        </div>
        <LabLibraryFeature session={session} userRecord={userRecord} />
      </div>
    );
  }

  return (
    <div style={{
      width: '100%', height: '100vh', overflowY: 'auto',
      background: GRID_BG,
      backgroundSize: '24px 24px',
      color: C.text1, fontFamily: FONT_SERIF,
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 32px 80px' }}>
        {activeProject ? (
          <ProjectWorkspace
            project={activeProject}
            onBack={() => setActiveProject(null)}
            session={session}
          />
        ) : (
          <>
            <Header userRecord={userRecord} onLibrary={() => setLabSection('library')} />
            <ProjectsSection
              projects={projects}
              onOpen={setActiveProject}
              onNewProject={() => setShowCreate(true)}
              userRecord={userRecord}
              accessToken={accessToken}
              onSeeded={loadProjects}
            />
            <DossierPreview />
            <Instruments />
            <MethodologyDiagram />
            <Notebook />
          </>
        )}

        {showCreate && (
          <CreateProjectModal
            accessToken={accessToken}
            onClose={() => setShowCreate(false)}
            onCreated={(p) => {
              setShowCreate(false);
              loadProjects();
              setActiveProject(p);
            }}
          />
        )}
      </div>
    </div>
  );
}

// ── Header — minimal, steel underline ─────────────────────
function Header({ userRecord, onLibrary }) {
  const handle = userRecord?.username
    ? `@${userRecord.username}`
    : (userRecord?.first_name || userRecord?.email?.split('@')[0] || 'researcher');
  return (
    <div style={{ marginBottom: 36, paddingBottom: 18, borderBottom: `1px solid ${C.steel}33` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
          <h1 style={{
            fontSize: 32, fontWeight: 600, color: C.text1,
            margin: 0, letterSpacing: '-0.01em',
          }}>
            Lab
          </h1>
          <span style={{ fontSize: 13, color: C.text3, fontFamily: FONT_SANS }}>
            {handle} · research workstation
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <StatusReadout />
          <button
            type="button"
            onClick={onLibrary}
            style={{
              padding: '6px 14px', fontSize: 12, fontFamily: FONT_SANS,
              background: `${C.gold}18`, border: `1px solid ${C.gold}44`,
              color: C.gold, borderRadius: 5, cursor: 'pointer',
              letterSpacing: '0.03em',
            }}
          >
            Shift Library →
          </button>
        </div>
      </div>
      <p style={{
        fontSize: 14, color: C.text2, marginTop: 14, marginBottom: 0,
        lineHeight: 1.55, maxWidth: 820,
      }}>
        Apply Shift's research methodology to any question worth investigating rigorously.
        Pre-register hypotheses · pull dossiers from public sources · run paired-case comparisons ·
        red-team your findings · log forward predictions for calibration.
      </p>
    </div>
  );
}

// Small instrument-style status readout
function StatusReadout() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      fontFamily: FONT_MONO, fontSize: 11, color: C.text4,
    }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.sage }} />
        METHODOLOGY · LOCKED
      </span>
      <span style={{ color: C.border3 }}>│</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.steel }} />
        11 SOURCES · ONLINE
      </span>
    </div>
  );
}

// ── ProjectsSection — list user's projects, or empty state ──
function ProjectsSection({ projects, onOpen, onNewProject, userRecord, accessToken, onSeeded }) {
  if (projects === null) {
    return (
      <div style={{ marginBottom: 44 }}>
        <SectionLabel label="Workbench" subtitle="Loading your research projects…" />
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <EmptyWorkbench
        onNewProject={onNewProject}
        userRecord={userRecord}
        accessToken={accessToken}
        onSeeded={onSeeded}
      />
    );
  }

  return (
    <div style={{ marginBottom: 44 }}>
      <SectionLabel
        label="Workbench"
        subtitle={`${projects.length} active research project${projects.length === 1 ? '' : 's'}.`}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button
          type="button"
          onClick={onNewProject}
          style={{
            padding: '8px 16px', fontSize: 13,
            background: 'transparent', color: C.gold,
            border: `1px solid ${C.gold}66`, borderRadius: 4,
            cursor: 'pointer', fontFamily: FONT_SERIF,
          }}
        >
          + New Research
        </button>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: 14,
      }}>
        {projects.map(p => <ProjectCard key={p.id} project={p} onOpen={onOpen} />)}
      </div>
    </div>
  );
}

function EmptyWorkbench({ onNewProject, userRecord, accessToken, onSeeded }) {
  const isAdmin = userRecord?.email === 'timmai1004@gmail.com';
  return (
    <div style={{ marginBottom: 44 }}>
      <SectionLabel label="Workbench" subtitle="Where your research projects sit. Empty until you start." />
      <div style={{
        background: C.bg2, border: `1px solid ${C.steel}40`,
        borderRadius: 8, padding: '40px 36px',
        position: 'relative', overflow: 'hidden',
      }}>
        <CornerTicks color={C.steel} />
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 1fr', gap: 36, alignItems: 'center' }}>
          <div>
            <div style={{
              fontSize: 11, color: C.steel, fontFamily: FONT_SANS,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              marginBottom: 10,
            }}>
              No active research
            </div>
            <h2 style={{
              fontSize: 24, fontWeight: 600, color: C.text1,
              margin: '0 0 14px 0', letterSpacing: '-0.005em',
            }}>
              Start your first research project.
            </h2>
            <p style={{
              fontSize: 14, color: C.text3, lineHeight: 1.6,
              margin: '0 0 22px 0',
            }}>
              Lab walks you through sharpening a research question, identifying paired
              cases, writing a falsifier, configuring sources, and registering your first
              hypothesis — all under the same methodology that distinguishes research from
              storytelling.
            </p>
            <button
              type="button"
              onClick={onNewProject}
              style={{
                padding: '12px 22px', fontSize: 14, fontWeight: 500,
                background: 'transparent', color: C.gold,
                border: `1px solid ${C.gold}66`, borderRadius: 5,
                cursor: 'pointer', fontFamily: FONT_SERIF,
                letterSpacing: '0.02em',
              }}
            >
              + Start your first research
            </button>
            <div style={{
              fontSize: 10, color: C.text4, marginTop: 12,
              fontFamily: FONT_MONO, letterSpacing: '0.08em',
            }}>
              MINIMAL CREATE FORM · WIZARD ARRIVES SESSION 7
            </div>
          </div>

          <ProjectSchematic />
        </div>
      </div>
      {isAdmin && (
        <AdminSeedButton accessToken={accessToken} onSeeded={onSeeded} />
      )}
    </div>
  );
}

function AdminSeedButton({ accessToken, onSeeded }) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const seed = async () => {
    if (!confirm('Seed Continuum starter content (Tier 1 cases + 10 H-Continuum-N hypotheses + 3 sources)?')) return;
    setRunning(true); setError(null);
    try {
      const res = await fetch('/api/lab/admin/seed-continuum', {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setResult(json);
      setTimeout(() => onSeeded(), 600);
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{
      marginTop: 14, padding: '12px 16px',
      background: C.bg3, border: `1px dashed ${C.steel}55`, borderRadius: 6,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, flexWrap: 'wrap',
    }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{
          fontSize: 10, color: C.steel, fontFamily: FONT_MONO,
          letterSpacing: '0.08em', marginBottom: 4,
        }}>
          ADMIN · ONE-TIME SEED
        </div>
        <div style={{ fontSize: 12, color: C.text3, lineHeight: 1.5 }}>
          {result
            ? `Seeded · project_id=${result.project_id} · ${(result.log || []).length} steps`
            : error
              ? <span style={{ color: C.terra }}>Error: {error}</span>
              : 'Imports your Continuum content (Tier 1 cases + H-Continuum-N hypotheses + 3 public sources). Idempotent — safe to re-run.'}
        </div>
      </div>
      <button
        type="button"
        onClick={seed}
        disabled={running || !!result}
        style={{
          padding: '8px 14px', fontSize: 12,
          background: 'transparent',
          color: running ? C.text4 : (result ? C.sage : C.steel),
          border: `1px solid ${result ? C.sage + '88' : C.steel + '88'}`,
          borderRadius: 4, fontFamily: FONT_SANS,
          cursor: running || result ? 'default' : 'pointer',
          letterSpacing: '0.05em',
        }}
      >
        {running ? 'Seeding…' : result ? '✓ Done' : 'Seed Continuum'}
      </button>
    </div>
  );
}

function ProjectCard({ project, onOpen }) {
  const s = project.stats || {};
  return (
    <button
      type="button"
      onClick={() => onOpen(project)}
      style={{
        background: C.bg2, border: `1px solid ${C.steel}33`,
        borderRadius: 8, padding: '18px 20px',
        cursor: 'pointer', textAlign: 'left',
        fontFamily: 'inherit', color: 'inherit',
        position: 'relative',
      }}
    >
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: C.steel, opacity: 0.45,
      }} />
      <div style={{ fontSize: 15, fontWeight: 600, color: C.text1, marginBottom: 6 }}>
        {project.name}
      </div>
      {project.question && (
        <div style={{
          fontSize: 12, color: C.text3, lineHeight: 1.5, marginBottom: 12,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {project.question}
        </div>
      )}
      <div style={{
        fontSize: 10, color: C.text4, fontFamily: FONT_MONO,
        letterSpacing: '0.05em', display: 'flex', gap: 12, flexWrap: 'wrap',
      }}>
        <span>{s.cases || 0} cases</span>
        <span>·</span>
        <span>{s.hypotheses || 0} hypotheses</span>
        <span>·</span>
        <span>{s.sources || 0} sources</span>
        <span>·</span>
        <span>{s.records || 0} records</span>
      </div>
    </button>
  );
}

// ── ProjectWorkspace — tabbed per-project surface ─────────
function ProjectWorkspace({ project, onBack, session }) {
  const [tab, setTab] = useState('cases');
  const accessToken = session?.access_token;

  const tabs = [
    { key: 'cases',       label: 'Cases',        live: true },
    { key: 'hypotheses',  label: 'Hypotheses',   live: true },
    { key: 'sources',     label: 'Sources',      live: true },
    { key: 'records',     label: 'Records',      live: false, sessionAt: 'Session 8' },
    { key: 'phase-a',     label: 'Phase A',      live: false, sessionAt: 'Session 8' },
    { key: 'benchmarks',  label: 'Benchmarks',   live: false, sessionAt: 'Session 7' },
    { key: 'trackrecord', label: 'Track Record', live: false, sessionAt: 'Session 9' },
  ];

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        style={{
          background: 'none', border: 'none', color: C.steel,
          cursor: 'pointer', fontFamily: FONT_SANS, fontSize: 12,
          padding: 0, marginBottom: 18,
          letterSpacing: '0.05em',
        }}
      >
        ← Back to Workbench
      </button>
      <h1 style={{
        fontSize: 28, fontWeight: 600, color: C.text1,
        margin: '0 0 8px 0', letterSpacing: '-0.01em',
      }}>
        {project.name}
      </h1>
      {project.question && (
        <p style={{ fontSize: 14, color: C.text3, lineHeight: 1.55, margin: '0 0 22px 0', maxWidth: 820 }}>
          {project.question}
        </p>
      )}

      {/* Tab bar */}
      <div style={{
        display: 'flex', borderBottom: `1px solid ${C.steel}33`,
        overflowX: 'auto', marginBottom: 24,
      }}>
        {tabs.map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => t.live && setTab(t.key)}
              disabled={!t.live}
              style={{
                padding: '10px 16px', fontSize: 13,
                background: 'none',
                border: 'none',
                borderBottom: `2px solid ${active ? C.gold : 'transparent'}`,
                color: active ? C.gold : (t.live ? C.text2 : C.text4),
                fontFamily: FONT_SERIF,
                cursor: t.live ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
                opacity: t.live ? 1 : 0.55,
              }}
            >
              {t.label}
              {!t.live && (
                <span style={{
                  marginLeft: 6, fontSize: 9, color: C.text4,
                  fontFamily: FONT_MONO, letterSpacing: '0.05em',
                }}>
                  · {t.sessionAt}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Active tab content */}
      {tab === 'cases'      && <CasesTab      project={project} accessToken={accessToken} />}
      {tab === 'hypotheses' && <HypothesesTab project={project} accessToken={accessToken} />}
      {tab === 'sources'    && <SourcesTab    project={project} accessToken={accessToken} />}
      {!['cases', 'hypotheses', 'sources'].includes(tab) && (
        <div style={{
          padding: '40px 24px', background: C.bg2,
          border: `1px solid ${C.border3}`, borderRadius: 6,
          color: C.text3, fontSize: 13, textAlign: 'center',
        }}>
          {tabs.find(t => t.key === tab)?.label} ships {tabs.find(t => t.key === tab)?.sessionAt}.
        </div>
      )}
    </div>
  );
}

// ── CasesTab ──────────────────────────────────────────────
// Exported so the Professional template's Simulator tab can lift
// this component wholesale per docs/PROFESSIONAL_TEMPLATE_SPEC.md §VI.
export function CasesTab({ project, accessToken }) {
  const [cases, setCases] = useState(null);
  const [openCaseId, setOpenCaseId] = useState(null);

  useEffect(() => {
    fetch(`/api/lab/cases?project_id=${project.id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => r.json())
      .then(j => setCases(j.cases || []))
      .catch(() => setCases([]));
  }, [project.id, accessToken]);

  if (cases === null) {
    return <LoadingPanel label="Loading cases…" />;
  }
  if (cases.length === 0) {
    return <EmptyTabPanel label="No cases yet." hint="Add cases via the wizard (Session 7)." />;
  }

  return (
    <div>
      <div style={{
        fontSize: 12, color: C.text4, fontFamily: FONT_SANS,
        letterSpacing: '0.05em', textTransform: 'uppercase',
        marginBottom: 12,
      }}>
        {cases.length} paired case{cases.length === 1 ? '' : 's'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {cases.map(c => (
          <CaseRow
            key={c.id}
            caseRow={c}
            isOpen={openCaseId === c.id}
            onToggle={() => setOpenCaseId(openCaseId === c.id ? null : c.id)}
          />
        ))}
      </div>
    </div>
  );
}

function CaseRow({ caseRow: c, isOpen, onToggle }) {
  return (
    <div style={{
      background: C.bg2, border: `1px solid ${isOpen ? C.steel + '88' : C.border3}`,
      borderRadius: 6, overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%', background: 'none', border: 'none',
          padding: '14px 18px', textAlign: 'left',
          fontFamily: 'inherit', color: 'inherit', cursor: 'pointer',
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gap: 12, alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text1, marginBottom: 4 }}>
            {c.label}
          </div>
          {c.isolates && (
            <div style={{ fontSize: 12, color: C.text3, lineHeight: 1.4 }}>
              {c.isolates}
            </div>
          )}
        </div>
        <div style={{
          fontSize: 10, color: C.text4, fontFamily: FONT_MONO,
          letterSpacing: '0.05em', textAlign: 'right',
        }}>
          {c.pair_logic && <div>{c.pair_logic.toUpperCase()}</div>}
          {c.hypothesis_id && <div style={{ marginTop: 4, color: C.steel }}>{c.hypothesis_id}</div>}
        </div>
        <div style={{
          fontSize: 10, color: C.text4, fontFamily: FONT_MONO,
          letterSpacing: '0.08em',
        }}>
          {isOpen ? '▼' : '▶'}
        </div>
      </button>
      {isOpen && <CaseDossierPanel caseRow={c} />}
    </div>
  );
}

function CaseDossierPanel({ caseRow: c }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dossier, setDossier] = useState(null);

  const labelClean = (c.label || '').replace(/\s+\d{4}.*$/, '').trim() || c.label;

  const pull = useCallback(async (force = false) => {
    setLoading(true); setError(null); setDossier(null);
    try {
      const res = await fetch('/api/lab/dossier', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          caseSpec: { type: c.case_type, id: c.case_external_id, label: labelClean },
          dateWindow: { startYear: c.start_year, endYear: c.end_year },
          force,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setDossier(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [c.case_type, c.case_external_id, c.start_year, c.end_year, labelClean]);

  useEffect(() => { pull(false); }, [pull]);

  return (
    <div style={{
      borderTop: `1px solid ${C.border3}`,
      padding: '14px 18px', background: C.bg3,
    }}>
      {loading && (
        <div style={{ fontSize: 12, color: C.text3, fontFamily: FONT_MONO }}>
          Pulling dossier from {labelClean}…
        </div>
      )}
      {error && (
        <div style={{
          padding: '10px 14px',
          background: `${C.terra}11`, border: `1px solid ${C.terra}55`,
          borderRadius: 4, color: C.terra, fontSize: 12,
          fontFamily: FONT_MONO,
        }}>
          Error: {error}
        </div>
      )}
      {dossier && (
        <>
          <div style={{
            display: 'flex', gap: 14, flexWrap: 'wrap',
            fontSize: 11, fontFamily: FONT_MONO, color: C.text3,
            paddingBottom: 10, borderBottom: `1px solid ${C.border3}`,
            marginBottom: 12,
          }}>
            {(dossier.sources_consulted || []).map((s, i) => {
              const recs = s.record_count ?? s.papers_returned ?? s.section_count ?? '—';
              return (
                <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{
                    width: 5, height: 5, borderRadius: '50%',
                    background: s.cache_hit ? C.sage : C.gold,
                  }} />
                  {s.source} ({recs}) {s.cache_hit ? 'cache' : 'live'}
                </span>
              );
            })}
            <button
              type="button"
              onClick={() => pull(true)}
              style={{
                marginLeft: 'auto', background: 'none', border: 'none',
                color: C.steel, fontFamily: FONT_MONO, fontSize: 11,
                cursor: 'pointer', letterSpacing: '0.05em',
              }}
            >
              ↻ pull fresh
            </button>
          </div>
          <div style={{
            fontSize: 12, lineHeight: 1.55, color: C.text2,
            whiteSpace: 'pre-wrap', fontFamily: FONT_MONO,
            maxHeight: '50vh', overflowY: 'auto',
          }}>
            {dossier.packet_markdown || '(empty)'}
          </div>
        </>
      )}
    </div>
  );
}

// ── HypothesesTab ─────────────────────────────────────────
// Exported for the Professional template's Simulator tab (Session 4
// of the Professional build sequence). Same component renders
// inside the Lab feature and the Professional Simulator.
export function HypothesesTab({ project, accessToken }) {
  const [hypotheses, setHypotheses] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    fetch(`/api/lab/hypotheses?project_id=${project.id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => r.json())
      .then(j => setHypotheses(j.hypotheses || []))
      .catch(() => setHypotheses([]));
  }, [project.id, accessToken]);

  if (hypotheses === null) return <LoadingPanel label="Loading hypotheses…" />;
  if (hypotheses.length === 0) return <EmptyTabPanel label="No hypotheses yet." hint="Register hypotheses via the wizard (Session 7)." />;

  return (
    <div>
      <div style={{
        fontSize: 12, color: C.text4, fontFamily: FONT_SANS,
        letterSpacing: '0.05em', textTransform: 'uppercase',
        marginBottom: 12,
      }}>
        {hypotheses.length} pre-registered hypothes{hypotheses.length === 1 ? 'is' : 'es'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {hypotheses.map(h => (
          <HypothesisRow
            key={h.id}
            h={h}
            isOpen={openId === h.id}
            onToggle={() => setOpenId(openId === h.id ? null : h.id)}
          />
        ))}
      </div>
    </div>
  );
}

function HypothesisRow({ h, isOpen, onToggle }) {
  const statusColor = {
    proposed: C.text4, active: C.gold, confirmed: C.sage,
    rejected: C.terra, respecified: C.steel, retired: C.text4,
  }[h.status] || C.text3;
  const phaseAColor = {
    'survived': C.sage, 'killed': C.terra,
    'survived-respecified': C.steel, 'not-yet-run': C.text4,
  }[h.phase_a_status] || C.text3;

  return (
    <div style={{
      background: C.bg2, border: `1px solid ${isOpen ? C.steel + '88' : C.border3}`,
      borderRadius: 6, overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%', background: 'none', border: 'none',
          padding: '14px 18px', textAlign: 'left',
          fontFamily: 'inherit', color: 'inherit', cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
          <span style={{
            fontSize: 13, fontWeight: 600, color: C.text1,
            fontFamily: FONT_MONO, letterSpacing: '0.02em',
          }}>
            {h.h_id}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Pill color={statusColor} label={h.status} />
            <Pill color={phaseAColor} label={`Phase A: ${h.phase_a_status}`} />
          </div>
        </div>
        <div style={{
          fontSize: 13, color: C.text2, lineHeight: 1.5,
          display: '-webkit-box', WebkitLineClamp: isOpen ? 'unset' : 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {h.claim}
        </div>
      </button>
      {isOpen && (
        <div style={{
          borderTop: `1px solid ${C.border3}`,
          padding: '14px 18px', background: C.bg3,
          fontSize: 12, lineHeight: 1.55, color: C.text3,
        }}>
          <DetailField label="Falsifier" value={h.falsifier} />
          <DetailField label="If confirmed" value={h.if_confirmed} />
          <DetailField label="If rejected" value={h.if_rejected} />
          {h.if_respecified && <DetailField label="If respecified" value={h.if_respecified} />}
          {h.pre_registered_at && (
            <div style={{ fontSize: 10, color: C.text4, fontFamily: FONT_MONO, marginTop: 8, letterSpacing: '0.05em' }}>
              PRE-REGISTERED · {new Date(h.pre_registered_at).toISOString().split('T')[0]}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        fontSize: 10, color: C.text4, fontFamily: FONT_SANS,
        letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: C.text2, lineHeight: 1.55 }}>
        {value}
      </div>
    </div>
  );
}

function Pill({ color, label }) {
  return (
    <span style={{
      fontSize: 9, padding: '2px 8px', borderRadius: 3,
      border: `1px solid ${color}`, color,
      textTransform: 'uppercase', letterSpacing: '0.05em',
      fontFamily: FONT_SANS, fontWeight: 600,
    }}>
      {label}
    </span>
  );
}

// ── SourcesTab ────────────────────────────────────────────
// Exported for the Professional template's Resources tab (Session 5
// of the Professional build sequence).
export function SourcesTab({ project, accessToken }) {
  const [sources, setSources] = useState(null);

  useEffect(() => {
    fetch(`/api/lab/sources?project_id=${project.id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => r.json())
      .then(j => setSources(j.sources || []))
      .catch(() => setSources([]));
  }, [project.id, accessToken]);

  const toggle = async (source) => {
    const res = await fetch('/api/lab/sources', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ id: source.id, enabled: !source.enabled }),
    });
    if (res.ok) {
      const j = await res.json();
      setSources(sources.map(s => s.id === source.id ? j.source : s));
    }
  };

  if (sources === null) return <LoadingPanel label="Loading sources…" />;

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'baseline', marginBottom: 12,
      }}>
        <span style={{
          fontSize: 12, color: C.text4, fontFamily: FONT_SANS,
          letterSpacing: '0.05em', textTransform: 'uppercase',
        }}>
          {sources.length} configured source{sources.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          disabled
          title="CSV / API / DB upload ships Session 6.5 + 8"
          style={{
            padding: '6px 12px', fontSize: 12,
            background: 'none', color: C.text4,
            border: `1px solid ${C.border3}`, borderRadius: 4,
            cursor: 'not-allowed', fontFamily: FONT_SERIF,
          }}
        >
          + Add resource · Session 6.5
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sources.map(s => (
          <div key={s.id} style={{
            background: C.bg2, border: `1px solid ${C.border3}`,
            borderRadius: 5, padding: '12px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text1, fontFamily: FONT_SANS }}>
                {s.display_name}
              </div>
              <div style={{
                fontSize: 10, color: C.text4, fontFamily: FONT_MONO,
                letterSpacing: '0.05em', marginTop: 4,
              }}>
                {s.source_type.toUpperCase()} · {s.source_key}
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggle(s)}
              style={{
                padding: '5px 12px', fontSize: 11,
                background: s.enabled ? `${C.sage}22` : 'transparent',
                color: s.enabled ? C.sage : C.text4,
                border: `1px solid ${s.enabled ? C.sage : C.border3}`,
                borderRadius: 4, fontFamily: FONT_SANS,
                cursor: 'pointer', letterSpacing: '0.05em',
              }}
            >
              {s.enabled ? 'enabled' : 'disabled'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab helpers ───────────────────────────────────────────
function LoadingPanel({ label }) {
  return (
    <div style={{
      padding: '40px 24px', background: C.bg2,
      border: `1px solid ${C.border3}`, borderRadius: 6,
      color: C.text3, fontSize: 13, textAlign: 'center',
    }}>
      {label}
    </div>
  );
}

function EmptyTabPanel({ label, hint }) {
  return (
    <div style={{
      padding: '40px 24px', background: C.bg2,
      border: `1px dashed ${C.border3}`, borderRadius: 6,
      color: C.text3, fontSize: 13, textAlign: 'center',
    }}>
      <div>{label}</div>
      {hint && <div style={{ marginTop: 6, fontSize: 11, color: C.text4 }}>{hint}</div>}
    </div>
  );
}

// ── CreateProjectModal — minimal form until wizard ships ──
function CreateProjectModal({ accessToken, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [question, setQuestion] = useState('');
  const [domainHint, setDomainHint] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true); setError(null);
    try {
      const res = await fetch('/api/lab/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          question: question.trim() || null,
          domain_hint: domainHint.trim() || null,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      onCreated(json.project);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 200, padding: 20,
    }}>
      <form onSubmit={submit} style={{
        background: C.bg2, border: `1px solid ${C.steel}66`,
        borderRadius: 8, padding: '28px 32px',
        width: '100%', maxWidth: 540, position: 'relative',
      }}>
        <CornerTicks color={C.steel} />
        <h2 style={{
          fontSize: 20, fontWeight: 600, color: C.text1,
          margin: '0 0 6px 0', fontFamily: FONT_SERIF,
        }}>
          New Research Project
        </h2>
        <p style={{ fontSize: 12, color: C.text4, margin: '0 0 22px 0', lineHeight: 1.5 }}>
          Minimal create form. The full wizard (Move 1 sharpen · Move 4 paired cases ·
          Move 5 falsifier · sources · privacy) ships Session 7.
        </p>

        <Field label="Project name (required)">
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Iran fertility reversal mechanism"
            style={inputStyle}
            autoFocus
          />
        </Field>

        <div style={{ marginTop: 14 }}>
          <Field label="Research question">
            <textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder="What are you investigating?"
              rows={3}
              style={{ ...inputStyle, fontFamily: FONT_SERIF, resize: 'vertical' }}
            />
          </Field>
        </div>

        <div style={{ marginTop: 14 }}>
          <Field label="Domain hint (optional)">
            <input
              value={domainHint}
              onChange={e => setDomainHint(e.target.value)}
              placeholder="e.g. population mechanism research"
              style={inputStyle}
            />
          </Field>
        </div>

        {error && (
          <div style={{
            marginTop: 14, padding: '8px 12px',
            background: `${C.terra}11`, border: `1px solid ${C.terra}55`,
            borderRadius: 4, color: C.terra, fontSize: 12,
          }}>
            {error}
          </div>
        )}

        <div style={{
          marginTop: 22, display: 'flex',
          gap: 8, justifyContent: 'flex-end',
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px', fontSize: 13,
              background: 'none', color: C.text3,
              border: `1px solid ${C.border3}`, borderRadius: 4,
              cursor: 'pointer', fontFamily: FONT_SERIF,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || submitting}
            style={{
              padding: '8px 18px', fontSize: 13,
              background: 'transparent',
              color: !name.trim() || submitting ? C.text4 : C.gold,
              border: `1px solid ${!name.trim() || submitting ? C.border3 : C.gold + '66'}`,
              borderRadius: 4,
              cursor: !name.trim() || submitting ? 'not-allowed' : 'pointer',
              fontFamily: FONT_SERIF,
            }}
          >
            {submitting ? 'Creating…' : 'Create project'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Right-column schematic — what a research project produces.
// Uses semantic colors only: sage = positive verdict, terra = adversarial,
// gold = locked output. Everything else is text-neutral with steel dots.
function ProjectSchematic() {
  const items = [
    { label: 'PAIRED CASES',         tone: 'neutral',   desc: 'with explicit pair logic' },
    { label: 'PRE-REG HYPOTHESES',   tone: 'neutral',   desc: 'falsifier per claim' },
    { label: 'DOSSIERS',             tone: 'neutral',   desc: 'fetched public sources' },
    { label: 'RED-TEAM RUNS',        tone: 'adverse',   desc: 'Phase A verdict' },
    { label: 'CANONICAL RECORDS',    tone: 'output',    desc: 'truth-stage stamped' },
    { label: 'FORWARD PREDICTIONS',  tone: 'positive',  desc: 'calibration logged' },
  ];
  const dotColor = {
    neutral:  C.steel,
    adverse:  C.terra,
    output:   C.gold,
    positive: C.sage,
  };
  return (
    <div style={{
      background: C.bg3, border: `1px solid ${C.border3}`,
      borderRadius: 6, padding: '20px 22px',
      fontFamily: FONT_MONO, fontSize: 11,
    }}>
      <div style={{
        color: C.text4, fontFamily: FONT_SANS,
        letterSpacing: '0.08em', textTransform: 'uppercase',
        fontSize: 10, marginBottom: 14,
      }}>
        A project produces
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {items.map(it => (
          <div key={it.label} style={{
            display: 'grid', gridTemplateColumns: '8px 160px 1fr', gap: 10, alignItems: 'center',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor[it.tone] }} />
            <span style={{ color: C.text2, letterSpacing: '0.05em' }}>{it.label}</span>
            <span style={{ color: C.text4, fontFamily: FONT_SERIF, fontStyle: 'italic', fontSize: 12 }}>{it.desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Instruments — public data sources Lab provides ────────
// All instruments rendered identically (steel = data/structure).
// Status indicator is sage (online) — would show terra if offline.
// Your-uploads tile uses gold to mark "your identity / private".
function Instruments() {
  const sources = [
    { key: 'worldbank',  label: 'World Bank',     desc: '1,400 indicators · 200 countries', kind: 'public' },
    { key: 'owid',       label: 'OWID',           desc: 'Charts + datasets',                kind: 'public' },
    { key: 'un-wpp',     label: 'UN WPP',         desc: 'Demographic projections',          kind: 'public' },
    { key: 'fred',       label: 'FRED',           desc: 'US economic data',                 kind: 'public' },
    { key: 'wikipedia',  label: 'Wikipedia',      desc: 'Article + event timelines',        kind: 'public' },
    { key: 'gdelt',      label: 'GDELT',          desc: 'Global event database',            kind: 'public' },
    { key: 'scholar',    label: 'Scholar',        desc: 'Paper abstracts + DOIs',           kind: 'public' },
    { key: 'arxiv',      label: 'arXiv / SSRN',   desc: 'Open-access papers',               kind: 'public' },
    { key: 'iucn',       label: 'IUCN / GBIF',    desc: 'Species + biodiversity',           kind: 'public' },
    { key: 'lpi',        label: 'LPI / eBird',    desc: 'Population trajectories',          kind: 'public' },
    { key: 'upload',     label: 'Your CSV / API', desc: 'Project-specific upload',          kind: 'private' },
  ];
  return (
    <div style={{ marginBottom: 44 }}>
      <SectionLabel label="Instruments" subtitle="Public data sources Lab wires for any project. Add your own CSV / API per project." />
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))',
        gap: 10,
      }}>
        {sources.map(s => <InstrumentTile key={s.key} {...s} />)}
      </div>
    </div>
  );
}

function InstrumentTile({ label, desc, kind }) {
  const accent = kind === 'private' ? C.gold : C.steel;
  return (
    <div style={{
      background: C.bg2, border: `1px solid ${C.border3}`,
      borderRadius: 5, padding: '12px 14px',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: accent, opacity: 0.55,
      }} />
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 6,
      }}>
        <span style={{
          fontSize: 13, fontWeight: 600, color: C.text1,
          fontFamily: FONT_SANS,
        }}>{label}</span>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.sage }} />
      </div>
      <div style={{
        fontSize: 11, color: C.text4, lineHeight: 1.4,
        fontFamily: FONT_SANS,
      }}>{desc}</div>
    </div>
  );
}

// ── Methodology diagram — schematic of the 10 moves ───────
// Steel = methodology baseline. Terra = Phase A (the verdict gate).
// Gold = the locked output (Q10 publish). No other colors used.
function MethodologyDiagram() {
  const moves = [
    { n: '1',  label: 'Sharpen Q',        tone: 'neutral' },
    { n: '2',  label: 'Map literature',   tone: 'neutral' },
    { n: '3',  label: 'Operationalize',   tone: 'neutral' },
    { n: '4',  label: 'Select cases',     tone: 'neutral', note: 'paired' },
    { n: '4b', label: 'Locate Δ-point',   tone: 'neutral' },
    { n: '5',  label: 'Falsifier',        tone: 'neutral', note: 'pre-reg' },
    { n: '6',  label: 'Process-trace',    tone: 'neutral' },
    { n: '7',  label: 'Counterfactual',   tone: 'neutral' },
    { n: '8',  label: 'Cross-domain',     tone: 'neutral' },
    { n: '9',  label: 'PHASE A',          tone: 'gate',    note: 'survives | killed | respec' },
    { n: '10', label: 'Publish + log',    tone: 'output' },
  ];
  const toneColor = {
    neutral: C.steel,
    gate:    C.terra,
    output:  C.gold,
  };
  return (
    <div style={{ marginBottom: 44 }}>
      <SectionLabel label="Methodology" subtitle="The 10 moves every project runs. Locked — universal across every Lab user." />
      <div style={{
        background: C.bg2, border: `1px solid ${C.border3}`,
        borderRadius: 8, padding: '24px 22px',
        position: 'relative', overflow: 'auto',
      }}>
        <CornerTicks color={C.steel} />
        <div style={{
          display: 'flex', alignItems: 'stretch', gap: 0,
          fontFamily: FONT_SANS, fontSize: 11,
          minWidth: 'max-content',
        }}>
          {moves.map((m, i) => {
            const c = toneColor[m.tone];
            const bold = m.tone === 'gate';
            return (
              <div key={m.n} style={{ display: 'flex', alignItems: 'stretch' }}>
                <div style={{
                  padding: '12px 14px',
                  background: bold ? `${c}11` : 'transparent',
                  border: `1px solid ${c}${bold ? '99' : '33'}`,
                  borderRadius: 4, minWidth: 100,
                  display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 4,
                }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{
                      color: c, fontWeight: 600, fontSize: 10,
                      fontFamily: FONT_MONO, letterSpacing: '0.05em',
                    }}>
                      Q{m.n}
                    </span>
                    {m.note && (
                      <span style={{ fontSize: 9, color: C.text4, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        {m.note}
                      </span>
                    )}
                  </div>
                  <span style={{
                    color: bold ? c : C.text2, fontWeight: bold ? 600 : 400,
                    fontSize: 12, lineHeight: 1.3,
                  }}>
                    {m.label}
                  </span>
                </div>
                {i < moves.length - 1 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', padding: '0 6px',
                    color: C.border3, fontSize: 14,
                  }}>→</div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{
          marginTop: 14, fontSize: 11, color: C.text4,
          fontFamily: FONT_SANS, lineHeight: 1.5,
        }}>
          Every record carries a confidence stamp per row · Every prediction logs to the calibration registry ·
          Findings that contradict each other trigger automatic reconciliation
        </div>
      </div>
    </div>
  );
}

// ── Notebook — orientation guides ─────────────────────────
// Uniform tile design — no per-tile accent colors. Steel left edge
// only. Lets content breathe; visual variety comes from typography.
function Notebook() {
  const guides = [
    { title: 'How Lab works',             blurb: 'The 10 research moves, confidence labels, paired-case discipline, and Phase A red-team — the methodology every Lab project follows.' },
    { title: 'Pre-registered hypotheses', blurb: 'How to write a falsifiable claim with a statistical test threshold and explicit if-confirmed / if-rejected branches.' },
    { title: 'Paired-case discipline',    blurb: 'Why solo cases are disallowed, the four pair logics (within-case / MSSD / MDSD / within-society), and how to choose a counter-case.' },
    { title: 'Phase A red-team',          blurb: 'Counter-Narrative + Outside View + Root Cause Engine — what survives the attack earns a confidence stamp; what doesn\'t gets killed or respec\'d.' },
    { title: 'Cross-domain analogs',      blurb: 'Equation-transfer vs structural-similarity vs metaphor — when an analog is research and when it\'s decoration.' },
    { title: 'Calibration registry',      blurb: 'Forward predictions logged with date + metric. The system gets calibrated by reality, not by rhetoric.' },
  ];
  return (
    <div>
      <SectionLabel label="Notebook" subtitle="Orientation. Read these before starting your first project." />
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 12,
      }}>
        {guides.map(g => (
          <div key={g.title} style={{
            background: C.bg2, border: `1px solid ${C.border3}`,
            borderRadius: 6, padding: '14px 16px',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', top: 0, bottom: 0, left: 0, width: 2,
              background: C.steel, opacity: 0.4,
            }} />
            <div style={{
              fontSize: 13, fontWeight: 600, color: C.text2,
              marginBottom: 6, fontFamily: FONT_SERIF,
              paddingLeft: 4,
            }}>
              {g.title}
            </div>
            <div style={{
              fontSize: 12, color: C.text4, lineHeight: 1.5,
              fontFamily: FONT_SANS, paddingLeft: 4,
            }}>
              {g.blurb}
            </div>
          </div>
        ))}
      </div>
      <div style={{
        marginTop: 18, fontSize: 10, color: C.text4,
        fontFamily: FONT_MONO, letterSpacing: '0.08em',
      }}>
        METHODOLOGY PAGES SHIP WITH WIZARD · PHASE 1 SESSION 7
      </div>
    </div>
  );
}

// ── Dossier Preview — collapsible debug/preview panel ────
// Lets researchers pull a dossier ad-hoc before the Session 6
// per-project workspace + Session 7 wizard land. Marked as a
// preview tool — eventually replaced by the proper UX.
function DossierPreview() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('country');
  const [id, setId] = useState('IR');
  const [label, setLabel] = useState('Iran');
  const [startYear, setStartYear] = useState(1986);
  const [endYear, setEndYear] = useState(2011);
  const [force, setForce] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dossier, setDossier] = useState(null);

  const pullDossier = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);
    setDossier(null);
    try {
      const res = await fetch('/api/lab/dossier', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          caseSpec: { type, id, label },
          dateWindow: { startYear: Number(startYear), endYear: Number(endYear) },
          force,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setDossier(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Common country presets — researcher can type any ISO-2 code
  const presets = [
    { id: 'IR', label: 'Iran',          startYear: 1986, endYear: 2011 },
    { id: 'KR', label: 'South Korea',   startYear: 1985, endYear: 2025 },
    { id: 'IL', label: 'Israel',        startYear: 1985, endYear: 2025 },
    { id: 'EG', label: 'Egypt',         startYear: 1986, endYear: 2011 },
    { id: 'IT', label: 'Italy',         startYear: 1985, endYear: 2025 },
  ];

  return (
    <div style={{ marginBottom: 44 }}>
      <SectionLabel
        label="Dossier preview"
        subtitle="Pull a dossier ad-hoc to see what the puller produces. The full per-project workspace ships Session 6; this is the preview-build affordance."
      />
      <div style={{
        background: C.bg2, border: `1px solid ${C.border3}`,
        borderRadius: 8, padding: '16px 18px',
        position: 'relative',
      }}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          style={{
            background: 'none', border: 'none',
            color: C.steel, cursor: 'pointer',
            fontFamily: FONT_SANS, fontSize: 12, fontWeight: 600,
            letterSpacing: '0.05em', textTransform: 'uppercase',
            padding: 0, display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <span style={{ fontSize: 10 }}>{open ? '▼' : '▶'}</span>
          {open ? 'Hide form' : 'Try a dossier pull'}
        </button>

        {open && (
          <div style={{ marginTop: 14 }}>
            {/* Preset row */}
            <div style={{ marginBottom: 14 }}>
              <div style={{
                fontSize: 10, color: C.text4, fontFamily: FONT_SANS,
                letterSpacing: '0.05em', textTransform: 'uppercase',
                marginBottom: 6,
              }}>
                Presets
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {presets.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setType('country'); setId(p.id); setLabel(p.label);
                      setStartYear(p.startYear); setEndYear(p.endYear);
                    }}
                    style={{
                      padding: '5px 10px', fontSize: 11,
                      background: 'transparent', color: C.text2,
                      border: `1px solid ${C.border3}`, borderRadius: 4,
                      cursor: 'pointer', fontFamily: FONT_SANS,
                    }}
                  >
                    {p.label} {p.startYear}–{p.endYear}
                  </button>
                ))}
              </div>
            </div>

            {/* Form */}
            <form onSubmit={pullDossier} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr auto',
              gap: 10, alignItems: 'end',
            }}>
              <Field label="Type">
                <select value={type} onChange={e => setType(e.target.value)} style={inputStyle}>
                  <option value="country">country</option>
                  <option value="region">region</option>
                  <option value="cohort">cohort</option>
                </select>
              </Field>
              <Field label="ID (ISO-2)">
                <input value={id} onChange={e => setId(e.target.value.toUpperCase())} maxLength={3} style={inputStyle} />
              </Field>
              <Field label="Label">
                <input value={label} onChange={e => setLabel(e.target.value)} style={inputStyle} />
              </Field>
              <Field label="Start year">
                <input type="number" value={startYear} onChange={e => setStartYear(e.target.value)} style={inputStyle} />
              </Field>
              <Field label="End year">
                <input type="number" value={endYear} onChange={e => setEndYear(e.target.value)} style={inputStyle} />
              </Field>
              <button type="submit" disabled={loading} style={{
                padding: '8px 16px', fontSize: 13,
                background: loading ? C.bg3 : 'transparent',
                color: loading ? C.text4 : C.gold,
                border: `1px solid ${C.gold}66`, borderRadius: 4,
                cursor: loading ? 'wait' : 'pointer', fontFamily: FONT_SERIF,
              }}>
                {loading ? 'Pulling…' : 'Pull dossier'}
              </button>
            </form>

            <label style={{
              display: 'flex', alignItems: 'center', gap: 6,
              marginTop: 10, fontSize: 11, color: C.text3,
              fontFamily: FONT_SANS, cursor: 'pointer',
            }}>
              <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} />
              Force fresh fetch (bypass cache)
            </label>

            {/* Status / error */}
            {error && (
              <div style={{
                marginTop: 14, padding: '10px 14px',
                background: `${C.terra}11`, border: `1px solid ${C.terra}55`,
                borderRadius: 4, color: C.terra, fontSize: 12,
                fontFamily: FONT_MONO,
              }}>
                Error: {error}
              </div>
            )}

            {/* Dossier result */}
            {dossier && <DossierResult dossier={dossier} />}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{
        fontSize: 10, color: C.text4, fontFamily: FONT_SANS,
        letterSpacing: '0.05em', textTransform: 'uppercase',
        marginBottom: 4,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '6px 9px', fontSize: 13,
  background: C.bg3, color: C.text1,
  border: `1px solid ${C.border3}`, borderRadius: 4,
  fontFamily: FONT_MONO,
};

function DossierResult({ dossier }) {
  return (
    <div style={{
      marginTop: 18, background: C.bg3,
      border: `1px solid ${C.steel}55`, borderRadius: 6,
      overflow: 'hidden',
    }}>
      {/* Status strip */}
      <div style={{
        padding: '10px 14px', borderBottom: `1px solid ${C.border3}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 12, color: C.text2, fontFamily: FONT_SERIF, fontWeight: 600 }}>
          {dossier.case_label}
        </div>
        <div style={{
          fontSize: 10, color: C.text4, fontFamily: FONT_MONO,
          letterSpacing: '0.05em',
        }}>
          {(dossier.sources_consulted || []).length} sources · {(dossier.unverified_sources || []).length} unverified · {dossier.packet_markdown?.length || 0} chars
        </div>
      </div>

      {/* Per-source readout */}
      <div style={{
        padding: '10px 14px', background: C.bg2,
        borderBottom: `1px solid ${C.border3}`,
        fontSize: 11, fontFamily: FONT_MONO, color: C.text3,
        display: 'flex', gap: 14, flexWrap: 'wrap',
      }}>
        {(dossier.sources_consulted || []).map((s, i) => {
          const recs = s.record_count ?? s.papers_returned ?? s.section_count ?? '—';
          return (
            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{
                width: 5, height: 5, borderRadius: '50%',
                background: s.cache_hit ? C.sage : C.gold,
              }} />
              {s.source} ({recs}) {s.cache_hit ? 'cache' : 'live'}
            </span>
          );
        })}
        {(dossier.unverified_sources || []).map((s, i) => (
          <span key={`u${i}`} style={{ display: 'flex', alignItems: 'center', gap: 5, color: C.terra }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.terra }} />
            {s} (unverified)
          </span>
        ))}
      </div>

      {/* Markdown body */}
      <div style={{
        padding: '14px 16px',
        fontSize: 12, lineHeight: 1.55, color: C.text2,
        whiteSpace: 'pre-wrap',
        fontFamily: FONT_MONO,
        maxHeight: '60vh', overflowY: 'auto',
      }}>
        {dossier.packet_markdown || '(empty)'}
      </div>
    </div>
  );
}

// ── Section label helper ──────────────────────────────────
function SectionLabel({ label, subtitle }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
        <span style={{
          fontSize: 11, color: C.steel, fontWeight: 600,
          fontFamily: FONT_SANS, letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}>
          {label}
        </span>
        <span style={{ flex: 1, height: 1, background: `${C.steel}22` }} />
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: C.text4, fontFamily: FONT_SANS, lineHeight: 1.5 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

// ── Corner ticks — instrument-panel decoration ────────────
function CornerTicks({ color }) {
  const tickStyle = (x, y) => ({
    position: 'absolute', [x]: 8, [y]: 8,
    width: 12, height: 12,
    borderTop:    y === 'top'    ? `1px solid ${color}55` : 'none',
    borderBottom: y === 'bottom' ? `1px solid ${color}55` : 'none',
    borderLeft:   x === 'left'   ? `1px solid ${color}55` : 'none',
    borderRight:  x === 'right'  ? `1px solid ${color}55` : 'none',
    pointerEvents: 'none',
  });
  return (
    <>
      <div style={tickStyle('left',  'top')} />
      <div style={tickStyle('right', 'top')} />
      <div style={tickStyle('left',  'bottom')} />
      <div style={tickStyle('right', 'bottom')} />
    </>
  );
}
